<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcInvoiceGenerationInvoiceLines',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({});

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const grid = ref(null);

  const $refs = {
    get grid() {
      return grid.value;
    },
  };

  const formatDataColumn = (...args) => {
    const [r, c, value, columnDefinition, row] = args;

    if (value == null) {
      return '';
    }

    const result = ['<div>'];

    if (value.net != null) {
      const net = number(value.net);
      result.push(`<div class="mb-1"><strong>${net}</strong></div>`);
    }

    if (value.quantity != null) {
      const quantity = number(value.quantity);
      result.push(`${quantity} ${row.unit}`);
    }

    result.push('</div>');
    return result.join('');
  };

  const resizeTrigger = ref(0);
  const selectedItems = ref([]);
  const ts = ref(null);
  const stockColumns = ref([
    {
      field: 'partner',
      name: $fn.value.pgettext('InvoiceGeneration|ColumnHeader', 'Partner'),
      type: 'command',
      formatter: {
        name: 'routeLink',
        to: 'contract',
        navigateOnIconClick: true,
        params: {
          id: 'partner_id',
        },
      },
    },
    {
      field: 'rental',
      name: $fn.value.pgettext('InvoiceGeneration|ColumnHeader', 'Rental'),
      type: 'command',
      formatter: {
        name: 'routeLink',
        to: 'rental',
        navigateOnIconClick: true,
        params: {
          id: 'rental_id',
        },
      },
    },
    {
      field: 'site',
      name: $fn.value.pgettext('InvoiceGeneration|ColumnHeader', 'Site'),
      width: '30px',
    },
    {
      field: 'item',
      name: $fn.value.pgettext('InvoiceGeneration|ColumnHeader', 'Item'),
      type: 'command',
      formatter: {
        name: 'routeLink',
        to: 'product',
        navigateOnIconClick: true,
        params: {
          id: 'product_id',
        },
      },
    },
    {
      field: 'serial_number',
      name: $fn.value.pgettext('InvoiceGeneration|ColumnHeader', 'Serial'),
      type: 'command',
      formatter: {
        name: 'routeLink',
        to: 'meter',
        navigateOnIconClick: true,
        params: {
          id: 'meter_id',
        },
      },
    },
  ]);
  const values = ref(null);
  const grouping = ref([
    {
      getter: 'partner',
      formatter: (g) => `<strong>${g.value}</strong>`,
      aggregateCollapsed: false,
      lazyTotalsCalculation: true,
    },
    {
      getter: 'rental',
      formatter: (g) => (g.value != null ? `<strong>${g.value}</strong>` : ''),
      aggregateCollapsed: false,
      lazyTotalsCalculation: true,
    },
  ]);
  const ux = ref([]);
  const b_2248226175642056_columnDefaults = computed(() => ({
    sortable: true,
    filterable: true,
    resizable: true,
  }));
  const b_2248226175642056_options = computed(() => ({
    rowHeight: 40,
    rowTopOffsetRenderType: 'transform',
  }));

  const $data = computed(() => ({
    resizeTrigger: resizeTrigger.value,
    selectedItems: selectedItems.value,
    ts: ts.value,
    stockColumns: stockColumns.value,
    values: values.value,
    grouping: grouping.value,
    ux: ux.value,
    b_2248226175642056_columnDefaults: b_2248226175642056_columnDefaults.value,
    b_2248226175642056_options: b_2248226175642056_options.value,
  }));

  const $$load_values = async () => {
    return $fn.value.fetch('view_draft_invoice_lines').then((p) =>
      _.extend(p, {
        columns: stockColumns.value.concat(
          p.columns.map((field) => ({
            field,
            name: field,
            textAlign: 'right',
            formatter: formatDataColumn,
          }))
        ),
      })
    );
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'resizeTrigger' },
      { name: 'selectedItems' },
      { name: 'ts' },
      { name: 'stockColumns' },
      { name: 'values' },
      { name: 'grouping' },
      { name: 'ux' },
      { name: 'b_2248226175642056_columnDefaults', nodeId: '2248226175642056' },
      { name: 'b_2248226175642056_options', nodeId: '2248226175642056' },
    ],
    loaders: { values: $$load_values },
    ux: {},
    dataRefs: {
      resizeTrigger,
      selectedItems,
      ts,
      stockColumns,
      values,
      grouping,
      ux,
      b_2248226175642056_columnDefaults,
      b_2248226175642056_options,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const act_2248226175642056_setData_qiogmcpot_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'selectedItems',
      value: $refs.grid.getSelectedItems(),
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_2248226175642056_showModal_bpkimoovr_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'Invoice',
      props: {
        oid: $event.data.data.id,
        modal: true,
      },
    };
  };
  const inst_evh_2248226175642056_command_w10meyr3s_getWhen = async (
    $event
  ) => {
    return $event.data.column.id === 'invoice_number';
  };
  const act_4615685607825208_reloadSlickgrid_s02h6priy_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'draftInvoices',
    };
  };
  const act_4615685607825208_request_tl6mwx26j_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'delete_draft_invoices',
      data: {
        invoices: selectedItems.value.map((p) => p.id),
      },
    };
  };

  const act_7315092382398562_reloadComponentData_9ovu8su66 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_yfgmdzlrs =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_9ovu8su66,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_yfgmdzlrs],
    event: 'reload',
  });
  const act_2248226175642056_setData_qiogmcpot = new actionTypes.SetDataAction({
    actionArgs: act_2248226175642056_setData_qiogmcpot_getActionArgs,
  });
  const inst_evh_2248226175642056_selectedRowsChanged_adric4n6c =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_setData_qiogmcpot,
      event: 'selectedRowsChanged',
      displayName: 'setData',
    });
  const inst_evh_2248226175642056_selectedRowsChanged =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2248226175642056_selectedRowsChanged_adric4n6c],
      event: 'selectedRowsChanged',
    });
  const act_2248226175642056_showModal_bpkimoovr =
    new actionTypes.ShowModalAction({
      actionArgs: act_2248226175642056_showModal_bpkimoovr_getActionArgs,
    });
  const inst_evh_2248226175642056_command_w10meyr3s =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_showModal_bpkimoovr,
      event: 'command',
      when: inst_evh_2248226175642056_command_w10meyr3s_getWhen,
      displayName: 'showModal',
    });
  const inst_evh_2248226175642056_command = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2248226175642056_command_w10meyr3s],
    event: 'command',
  });
  const act_4615685607825208_reloadSlickgrid_s02h6priy =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_4615685607825208_reloadSlickgrid_s02h6priy_getActionArgs,
    });
  const inst_evh_4615685607825208_success_9or0oymti =
    new actionTypes.EventHandlerImpl({
      action: act_4615685607825208_reloadSlickgrid_s02h6priy,
      event: 'success',
      displayName: 'reloadSlickgrid',
    });
  const act_4615685607825208_request_tl6mwx26j = new actionTypes.RequestAction({
    actionArgs: act_4615685607825208_request_tl6mwx26j_getActionArgs,
    displayName: 'deleteItems',
    events: [inst_evh_4615685607825208_success_9or0oymti],
  });
  const inst_evh_4615685607825208_click_0mh26wzyh =
    new actionTypes.EventHandlerImpl({
      action: act_4615685607825208_request_tl6mwx26j,
      event: 'click',
      displayName: 'deleteItems',
    });
  const inst_evh_4615685607825208_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_4615685607825208_click_0mh26wzyh],
    event: 'click',
  });

  const actions = {
    inst_evh_7315092382398562_reload_yfgmdzlrs,
    inst_evh_7315092382398562_reload,
    inst_evh_2248226175642056_selectedRowsChanged_adric4n6c,
    inst_evh_2248226175642056_selectedRowsChanged,
    inst_evh_2248226175642056_command_w10meyr3s,
    inst_evh_2248226175642056_command,
    inst_evh_4615685607825208_success_9or0oymti,
    inst_evh_4615685607825208_click_0mh26wzyh,
    deleteItems: inst_evh_4615685607825208_click_0mh26wzyh,
    inst_evh_4615685607825208_click,
  };

  const evh_7315092382398562_reload_yfgmdzlrs = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_yfgmdzlrs.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged_adric4n6c = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged_adric4n6c.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_command_w10meyr3s = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_command_w10meyr3s.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_command = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_command.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_success_9or0oymti = async (event, scope) => {
    return await actions.inst_evh_4615685607825208_success_9or0oymti.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_click_0mh26wzyh = async (event, scope) => {
    return await actions.inst_evh_4615685607825208_click_0mh26wzyh.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_click = async (event, scope) => {
    return await actions.inst_evh_4615685607825208_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <VueSlickgrid
      name="draftInvoiceLines"
      :resizeTrigger="resizeTrigger"
      :headerTitle="gettext('Draft invoice lines')"
      :items="values.rows"
      checkbox-row-selection
      footer-totals
      export-to-csv
      :exportToCsvFilename="pgettext('ExportFilename', 'draft-invoice-lines')"
      :columnDefaults="b_2248226175642056_columnDefaults"
      :columns="values.columns"
      :options="b_2248226175642056_options"
      :grouping="grouping"
      ref="grid"
      v-if="values && grouping"
      @selectedRowsChanged="
        evh_2248226175642056_selectedRowsChanged($event, {})
      "
      @command="evh_2248226175642056_command($event, {})"
    >
      <template #toolbar>
        <div class="flex-grow-1 d-flex gap-2">
          <ItpButton
            icon="fa-trash"
            :text="gettext('Delete')"
            size="sm"
            variant="light"
            :disabled="!selectedItems || !selectedItems.length"
            :spinning="actions.deleteItems.isRunning"
            @click="evh_4615685607825208_click($event, {})"
          >
          </ItpButton>
        </div>
      </template>
    </VueSlickgrid>
  </ItpUsercontrol>
</template>
